import React, { useState, useEffect } from 'react'
import { IntlProvider } from 'react-intl'
import enTranslation from './en.json'
import thTranslation from './th.json'
import ruTranslation from './ru.json'
import hyTranslation from './hy.json'
// import arTranslation from './ar.json';
import esTranslation from './es.json'
import flatten from 'flat'
import effects from 'app/effects/effects.js'
import * as R from 'ramda'
import { useUserProfile } from 'commons/useUserProfile'
import { useDispatch } from 'react-redux'
import * as Actions from 'app/store/actions'
import Cookies from 'js-cookie'

export const IntlContext = React.createContext()

export const locales = {
  en: 'en',
  th: 'th',
  ru: 'ru',
  // hy: 'hy',
  // ar: 'ar',
  es: 'es',
}

export const translations = {
  [locales.en]: { value: enTranslation, label: 'English', direction: 'ltr' },
  [locales.th]: { value: thTranslation, label: 'ภาษาไทย', direction: 'ltr' },
  [locales.ru]: {
    value: ruTranslation,
    label: 'русский язык',
    direction: 'ltr',
  },
  [locales.hy]: { value: hyTranslation, label: 'հայերեն', direction: 'ltr' },
  // [locales.ar]: { value: arTranslation, label: 'العربية', direction: 'rtl' },
  [locales.es]: { value: esTranslation, label: 'español', direction: 'ltr' },
}

const STORAGE_KEY = 'locale'

const getBrowserLocale = () =>
  (navigator.languages && R.head(navigator.languages)) ||
  navigator.language ||
  navigator.userLanguage

const getDefaultLocale = () => {
  return R.unless(
    R.includes(R.__, R.keys(locales)),
    R.always(locales.en),
    R.unless(R.isNil, R.take(2), getBrowserLocale()),
  )
}

const hasLocale = (locale) =>
  R.compose(
    R.not,
    R.isEmpty,
    R.filter((item) => item === locale),
  )(locales)

const localeCookieKey = '_ms_manager_locale'
function getCookieDomain(host) {
  if (host === 'localhost') return host
  const parts = host.split('.')
  return '.' + parts.slice(-2).join('.')
}
function setLocaleCookie(locale) {
  const host = window.location.hostname
  Cookies.set(localeCookieKey, locale, {
    domain: getCookieDomain(host),
    path: '/',
    sameSite: 'lax',
    expires: 31536000,
  })
}

const saveLocale = (locale) => {
  effects.localStorage.setItem(STORAGE_KEY, locale)
  setLocaleCookie(locale)
}
export const getLocale = () =>
  hasLocale(effects.localStorage.getItem(STORAGE_KEY))
    ? effects.localStorage.getItem(STORAGE_KEY)
    : locales.en

export const clearLocale = () => effects.localStorage.removeItem(STORAGE_KEY)

export const LocalizationProvider = (props) => {
  const dispatch = useDispatch()
  const currentUserProfile = useUserProfile()

  const [locale, setLocale] = useState(
    R.when(R.isNil, getDefaultLocale, getLocale()),
  )

  const [messages, setMessages] = useState(flatten(translations[locale].value))

  const switchToLanguage = (newLocale) => {
    if (hasLocale(newLocale)) {
      setLocale(newLocale)
      setMessages(
        R.mergeLeft(
          translations[newLocale].value,
          translations[locales.en].value,
        ),
      )
      saveLocale(newLocale)
      dispatch(
        Actions.setDirection({ direction: translations[newLocale].direction }),
      )
    }
  }

  const updateLocalizationLanguage = ({ locale, items }) => {
    const model = {
      firstName: currentUserProfile.firstName,
      lastName: currentUserProfile.lastName,
      email: currentUserProfile.email,
      languageId: R.head(currentUserProfile.defaultLanguage).id,
      countryCode: currentUserProfile.countryCode,
      phoneNumber: currentUserProfile.phoneNumber,
      localizationLanguageId: R.prop(
        'id',
        R.find(R.propEq('code', locale))(items),
      ),
    }
    effects.apis.updateAdminProfile(model)
  }

  const localizationLanguage = R.prop(
    'localizationLanguage',
    currentUserProfile,
  )
  useEffect(() => {
    if (localizationLanguage) {
      switchToLanguage(localizationLanguage.code)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localizationLanguage])

  return (
    <IntlContext.Provider
      value={{ locale, messages, switchToLanguage, updateLocalizationLanguage }}
    >
      <IntlProvider
        locale={locale}
        messages={messages}
        defaultLocale="en"
        {...props}
      />
    </IntlContext.Provider>
  )
}

export const useIntlContextState = () => {
  const context = React.useContext(IntlContext)
  if (context === undefined) {
    throw new Error(`IntlContext must be used within a LocalizationProvider`)
  }
  return context
}
